import type { Ayah } from '@app-types/KuranTypes'
import type { PageData } from '@components/shared/PageInfoRow/PageInfoRow'

const RESPONSE_EVENT = '__hayirhahNativeQuranResponse'

type NativeBridgeWindow = Window & {
  ReactNativeWebView?: { postMessage?: (_message: string) => void }
  __HAYIRHAH_NATIVE__?: {
    requestQuranPage?: (_routePage: number, _requestId: string) => void
  }
}

type NativeQuranResponse = {
  requestId: string
  routePage: number
  page: { ayahs: Ayah[]; pageData: PageData } | null
}

export type NativeQuranPage = {
  routePage: number
  ayahs: Ayah[]
  pageData: PageData
}

/** Checks the zero-based route boundary understood by the native content package. */
const isValidRoutePage = (routePage: number): boolean =>
  Number.isInteger(routePage) && routePage >= 0 && routePage <= 604

const getNativeWindow = (): NativeBridgeWindow | null =>
  typeof window === 'undefined' ? null : (window as NativeBridgeWindow)

export const hasNativeQuranBridge = (): boolean => {
  const nativeWindow = getNativeWindow()
  return Boolean(
    nativeWindow?.__HAYIRHAH_NATIVE__?.requestQuranPage ||
    nativeWindow?.ReactNativeWebView?.postMessage,
  )
}

/** Requests one Quran page from the React Native bundle instead of the backend. */
export function getNativeQuranPage(
  routePage: number,
  timeoutMs = 2500,
): Promise<NativeQuranPage | null> {
  const nativeWindow = getNativeWindow()
  if (
    !nativeWindow ||
    !hasNativeQuranBridge() ||
    !isValidRoutePage(routePage) ||
    timeoutMs <= 0
  ) {
    return Promise.resolve(null)
  }

  const requestId = `quran-${routePage}-${Date.now()}-${Math.random().toString(36).slice(2)}`

  return new Promise((resolve) => {
    let settled = false
    const finish = (value: NativeQuranPage | null) => {
      if (settled) return
      settled = true
      window.removeEventListener(RESPONSE_EVENT, handleResponse as EventListener)
      window.clearTimeout(timeoutId)
      resolve(value)
    }

    const handleResponse = (event: Event) => {
      const response = (event as CustomEvent<NativeQuranResponse>).detail
      if (!response || response.requestId !== requestId) return
      if (
        !response.page ||
        response.routePage !== routePage ||
        !Array.isArray(response.page.ayahs) ||
        typeof response.page.pageData !== 'object' ||
        response.page.pageData === null
      ) {
        finish(null)
        return
      }
      finish({
        routePage,
        ayahs: response.page.ayahs,
        pageData: response.page.pageData,
      })
    }

    const timeoutId = window.setTimeout(() => finish(null), timeoutMs)
    window.addEventListener(RESPONSE_EVENT, handleResponse as EventListener)

    try {
      if (nativeWindow.__HAYIRHAH_NATIVE__?.requestQuranPage) {
        nativeWindow.__HAYIRHAH_NATIVE__.requestQuranPage(routePage, requestId)
        return
      }
      nativeWindow.ReactNativeWebView?.postMessage?.(
        JSON.stringify({ type: 'getQuranPage', routePage, requestId }),
      )
    } catch {
      finish(null)
    }
  })
}
